import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { DownloadOutlined } from '@ant-design/icons';
import { Alert, AutoComplete, Button, Checkbox, Collapse, DatePicker, Empty, Modal, Select, Spin, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';

/**
 * Bank orders for one payout of a closed AB payroll month, as a Halcom TXT the
 * company imports into its e-banking: one order per worker account (code 240,
 * model 97 with the BOP of the worker's PPP-PD) and, optionally, one per
 * PPP-PD for tax and contributions (code 254). Every amount is AB's own; the
 * window only shows what goes in and what cannot, and why.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {string} props.companyId
 */
const money = (n) =>
  Number(n || 0).toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const periodLabel = (period) => `${period.slice(4, 6)}.${period.slice(0, 4)}`;

const payoutKey = (p) => `${p.period}|${p.payment}`;

// Domestic account control digits (MOD 97-10): a broken profile account is not offered.
const isValidAccount = (digits) => /^\d{18}$/.test(digits || '') && BigInt(digits) % 97n === 1n;

export default function SalaryPaymentOrdersModal({ open, onClose, companyId }) {
  const { t } = useTranslation(['forms', 'messages']);
  const [selected, setSelected] = useState(null);
  const [debtorAccount, setDebtorAccount] = useState(null);
  const [executionDate, setExecutionDate] = useState(dayjs());
  // PPP-PD filings whose tax order stays out (paid separately); all go in by default.
  const [skippedFilings, setSkippedFilings] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const { data: list, isLoading: listLoading } = useQuery({
    queryKey: ['salary-payment-payouts', companyId],
    queryFn: () => API.exports.checkSalaryPayment({ companyId }),
    enabled: Boolean(open && companyId),
  });
  const payouts = useMemo(() => list?.payouts || [], [list]);

  // Newest payout first: usually the month that was just closed.
  useEffect(() => {
    if (!selected && payouts.length) setSelected(payoutKey(payouts[0]));
  }, [payouts, selected]);

  const [period, payment] = selected ? selected.split('|') : [null, null];
  useEffect(() => setSkippedFilings([]), [selected]);

  // The server returns every filing's order; leaving some out is only a filter here.
  const { data: preview, isFetching: previewLoading } = useQuery({
    queryKey: ['salary-payment-preview', companyId, period, payment],
    queryFn: () => API.exports.checkSalaryPayment({ companyId, period, payment: Number(payment) }),
    enabled: Boolean(open && companyId && period),
  });
  const orders = useMemo(
    () => (preview?.orders || []).filter((o) => o.kind !== 'tax' || !skippedFilings.includes(o.filing)),
    [preview, skippedFilings]
  );
  const salaryTotal = orders.filter((o) => o.kind === 'salary').reduce((x, o) => x + o.amount, 0);
  const taxTotal = orders.filter((o) => o.kind === 'tax').reduce((x, o) => x + o.amount, 0);
  const toggleFiling = (filingNo, on) =>
    setSkippedFilings((list) => (on ? list.filter((n) => n !== filingNo) : [...list, filingNo]));

  const { data: paying } = useQuery({
    queryKey: ['halcom-payment-accounts', companyId],
    queryFn: () => API.exports.getHalcomPaymentAccounts(companyId),
    enabled: Boolean(open && companyId),
    staleTime: 5 * 60 * 1000,
  });
  const lastAccount = list?.lastDebtorAccount || '';
  const accountOptions = useMemo(
    () => [
      // The account the last salary file was paid from comes first: it is often not on the profile.
      ...(lastAccount ? [{ value: lastAccount, label: `${lastAccount} · ${t('salaryOrdersLastUsed')}` }] : []),
      ...(paying?.accounts || [])
        .filter((a) => isValidAccount(a.account) && a.display !== lastAccount)
        .map((a) => ({
        value: a.display,
        label: `${a.display}${a.bank ? ` · ${a.bank}` : ''}${a.projectCode ? ` · ${a.projectCode}` : ''}`,
      })),
    ],
    [paying, lastAccount, t]
  );
  // Follows the first option (the last used account once it is known) until somebody picks one.
  const [accountTouched, setAccountTouched] = useState(false);
  useEffect(() => {
    if (!accountTouched && accountOptions.length) setDebtorAccount(accountOptions[0].value);
  }, [accountOptions, accountTouched]);

  const blocking = (preview?.problems || []).filter((p) => !p.warning);
  const warnings = (preview?.problems || []).filter((p) => p.warning);

  const payoutOptions = payouts.map((p) => ({
    value: payoutKey(p),
    label:
      p.payment === 0
        ? t('salaryOrdersPayoutSalary', {
            month: periodLabel(p.period),
            date: p.paidOn ? dayjs(p.paidOn).format('DD.MM.YYYY') : '—',
          })
        : t('salaryOrdersPayoutOther', { month: periodLabel(p.period), number: p.payment }),
  }));

  const problemText = (p) =>
    t(`salaryOrdersProblem_${p.reason}`, {
      worker: p.worker || '',
      amount: money(p.amount),
      account: p.account || '',
      shares: p.shares,
      filing: p.filing,
    });

  const columns = useMemo(
    () => [
      {
        title: t('salaryOrdersRecipient'),
        key: 'name',
        ellipsis: true,
        render: (_, o) =>
          o.kind === 'tax' ? (
            <span>
              <Tag color="blue">{t('salaryOrdersTaxTag', { filing: o.filing })}</Tag>
              {o.name}
            </span>
          ) : (
            o.name
          ),
      },
      { title: t('salaryOrdersAccount'), dataIndex: 'account', key: 'account', width: 200 },
      {
        title: t('salaryOrdersCodeRef'),
        key: 'ref',
        width: 230,
        render: (_, o) => `${o.code} · ${o.model} ${o.ref}`,
      },
      {
        title: t('salaryOrdersAmount'),
        key: 'amount',
        width: 150,
        align: 'right',
        render: (_, o) => (
          <span>
            {o.share !== undefined && o.share !== 100 && <Tag className="mr-1">{`${o.share}%`}</Tag>}
            {money(o.amount)}
          </span>
        ),
      },
    ],
    [t]
  );

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      await API.exports.exportSalaryPayment({
        companyId,
        period,
        payment: Number(payment),
        taxFilings: (preview?.filings || [])
          .map((f) => f.filingNo)
          .filter((n) => !skippedFilings.includes(n)),
        debtorAccount,
        executionDate: executionDate.format('YYYY-MM-DD'),
      });
      message.success(t('salaryOrdersDownloaded', { ns: 'messages' }));
      onClose();
    } catch (err) {
      const data = err?.response?.data;
      let text = data instanceof Blob ? await data.text() : data?.message || err?.message;
      try {
        // The specific reason (`error`) says more than the general message.
        const body = JSON.parse(text);
        text = body.error || body.message || text;
      } catch {
        // plain text already
      }
      setError(text || t('salaryOrdersError', { ns: 'messages' }));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      title={t('salaryOrdersTitle')}
      open={open}
      onCancel={onClose}
      width={1000}
      destroyOnClose
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button className="btn-default" onClick={onClose}>
            {t('cancel', { ns: 'messages' })}
          </Button>
          <Button
            type="primary"
            className="btn-primary"
            icon={<DownloadOutlined />}
            loading={downloading}
            disabled={!orders.length || blocking.length > 0 || !debtorAccount || !executionDate}
            onClick={handleDownload}
          >
            {t('downloadTxt')}
          </Button>
        </div>
      }
    >
      {listLoading ? (
        <div className="flex justify-center py-10">
          <Spin />
        </div>
      ) : !payouts.length ? (
        <Empty description={t('salaryOrdersNoPayouts')} />
      ) : (
        <>
          <Alert type="info" showIcon className="mb-4" message={t('salaryOrdersInfo')} />

          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <div className="mb-1 text-sm text-gray-500">{t('salaryOrdersPayout')}</div>
              <Select className="w-full" value={selected} options={payoutOptions} onChange={setSelected} />
            </div>
            <div>
              <div className="mb-1 text-sm text-gray-500">{t('salaryOrdersPayFrom')}</div>
              {/* The salary account is often not the one on the company profile: it can be typed in. */}
              <AutoComplete
                className="w-full"
                value={debtorAccount}
                options={accountOptions}
                onChange={(v) => {
                  setAccountTouched(true);
                  setDebtorAccount(v);
                }}
                placeholder={t('salaryOrdersAccountPlaceholder')}
              />
            </div>
            <div>
              <div className="mb-1 text-sm text-gray-500">{t('salaryOrdersExecutionDate')}</div>
              <DatePicker
                className="w-full"
                value={executionDate}
                onChange={setExecutionDate}
                format="DD.MM.YYYY"
                allowClear={false}
              />
            </div>
          </div>

          {preview?.filings?.length > 0 && (
            <div className="mb-4">
              <div className="mb-1 text-sm text-gray-500">{t('salaryOrdersIncludeTax')}</div>
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                {preview.filings.map((f) => (
                  <Checkbox
                    key={f.filingNo}
                    checked={!skippedFilings.includes(f.filingNo)}
                    onChange={(e) => toggleFiling(f.filingNo, e.target.checked)}
                  >
                    {t('salaryOrdersFiling', { filing: f.filingNo, amount: money(f.total), count: f.employees })}
                  </Checkbox>
                ))}
              </div>
            </div>
          )}

          {blocking.length > 0 && (
            <Alert
              type="error"
              showIcon
              className="mb-3"
              message={t('salaryOrdersBlocked')}
              description={
                <ul className="m-0 list-disc pl-5">
                  {blocking.map((p, i) => (
                    <li key={i}>{problemText(p)}</li>
                  ))}
                </ul>
              }
            />
          )}
          {warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message={
                <ul className="m-0 list-disc pl-5">
                  {warnings.map((p, i) => (
                    <li key={i}>{problemText(p)}</li>
                  ))}
                </ul>
              }
            />
          )}

          {preview && (
            <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span>{t('salaryOrdersCount', { count: orders.length })}</span>
              <span>
                {t('salaryOrdersSalaryTotal')}: <b>{money(salaryTotal)}</b>
              </span>
              <span>
                {t('salaryOrdersTaxTotal')}: <b>{money(taxTotal)}</b>
              </span>
              <span>
                {t('salaryOrdersTotal')}: <b>{money(salaryTotal + taxTotal)}</b>
              </span>
            </div>
          )}

          <Table
            size="small"
            rowKey={(o, i) => `${o.kind}-${i}`}
            columns={columns}
            dataSource={orders}
            loading={previewLoading}
            pagination={{ pageSize: 15, showSizeChanger: false }}
          />

          {preview?.deductions?.length > 0 && (
            <Collapse
              className="mt-3"
              size="small"
              items={[
                {
                  key: 'deductions',
                  label: t('salaryOrdersDeductions', { count: preview.deductions.length }),
                  children: (
                    <ul className="m-0 list-disc pl-5 text-sm">
                      {preview.deductions.map((d, i) => (
                        <li key={i}>{`${d.worker} · ${d.code} · ${money(d.amount)}`}</li>
                      ))}
                    </ul>
                  ),
                },
              ]}
            />
          )}

          {error && <Alert type="error" showIcon className="mt-3" message={error} />}
        </>
      )}
    </Modal>
  );
}
